import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, HashRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { NativeStandProvider } from "@/context/NativeStandContext";
import { NativeOfflineProvider } from "@/context/NativeOfflineContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import RouteFallback from "@/components/RouteFallback";

// Eager: shell + auth pages (small, needed first paint)
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import MagicLogin from "@/pages/MagicLogin";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AccountDeletion from "@/pages/AccountDeletion";
import Privacy from "@/pages/Privacy";
import Welcome from "@/pages/Welcome";
import AccountRequired from "@/pages/AccountRequired";
import FeatureRoute from "@/components/FeatureRoute";
import { getHiveflowConfig } from "@/lib/environment";

// Lazy: all in-app pages (split into separate chunks → faster first paint)
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Apiaries = lazy(() => import("@/pages/Apiaries"));
const ApiaryDetail = lazy(() => import("@/pages/ApiaryDetail"));
const Hives = lazy(() => import("@/pages/Hives"));
const HiveDetail = lazy(() => import("@/pages/HiveDetail"));
const InspectionForm = lazy(() => import("@/pages/InspectionForm"));
const Planning = lazy(() => import("@/pages/Planning"));
const Queens = lazy(() => import("@/pages/Queens"));
const Breeding = lazy(() => import("@/pages/Breeding"));
const More = lazy(() => import("@/pages/More"));
const Calendar = lazy(() => import("@/pages/Calendar"));
const Team = lazy(() => import("@/pages/Team"));
const Export = lazy(() => import("@/pages/Export"));
const Trash = lazy(() => import("@/pages/Trash"));

function App() {
  const Router = getHiveflowConfig().offlineBundle ? HashRouter : BrowserRouter;
  return (
    <div className="App">
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <Toaster position="top-center" richColors />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/welcome" element={<Welcome />} />
              <Route path="/register" element={<Register />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/magic-login" element={<MagicLogin />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/account-verwijderen" element={<AccountDeletion />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/" element={<ProtectedRoute><NativeOfflineProvider><NativeStandProvider><AppShell /></NativeStandProvider></NativeOfflineProvider></ProtectedRoute>}>
                <Route index element={<Dashboard />} />
                <Route path="bijenstanden" element={<Apiaries />} />
                <Route path="bijenstanden/:id" element={<ApiaryDetail />} />
                <Route path="kasten" element={<Hives />} />
                <Route path="kasten/:id" element={<HiveDetail />} />
                <Route path="inspectie/nieuw" element={<InspectionForm />} />
                <Route path="planning" element={<Planning />} />
                <Route path="koninginnen" element={<FeatureRoute feature="queenBreeding"><Queens /></FeatureRoute>} />
                <Route path="teelt" element={<FeatureRoute feature="queenBreeding"><Breeding /></FeatureRoute>} />
                <Route path="voorstellen" element={<Navigate to="/planning?view=voorstellen" replace />} />
                <Route path="kalender" element={<Calendar />} />
                <Route path="account-nodig" element={<AccountRequired />} />
                <Route path="team" element={<FeatureRoute feature="teams"><Team /></FeatureRoute>} />
                <Route path="export" element={<FeatureRoute feature="export"><Export /></FeatureRoute>} />
                <Route path="prullenbak" element={<Trash />} />
                <Route path="meer" element={<More />} />
              </Route>
            </Routes>
          </Suspense>
        </Router>
      </AuthProvider>
    </div>
  );
}

export default App;
